import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Trophy, Award, Lock, Check } from 'lucide-react';
import { format } from 'date-fns';

interface Achievement {
  id: string;
  name: string;
  description: string;
  points: number;
  criteria?: Record<string, any>;
}

interface UserAchievement {
  id: string;
  achievement: Achievement;
  earned_at: string;
}

interface UserMetrics {
  totalRuns?: number;
  totalMiles?: number;
  trailsCompletedCount?: number;
  photosShared?: number;
  waypointsShared?: number;
  activeMonthsThisYear?: {
    count: number;
    total: number;
  };
}

interface ProfileAchievementsProps {
  earnedAchievements?: UserAchievement[];
  availableAchievements?: Achievement[];
  isLoading?: boolean;
  userMetrics?: UserMetrics;
}

type FilterType = 'all' | 'completed' | 'incomplete';

// Format criteria into human-readable text
const formatCriteria = (criteria: Record<string, any> | undefined): string => {
  if (!criteria) return '';
  
  const parts: string[] = [];
  
  if (criteria.runs) {
    parts.push(`Complete ${criteria.runs} run${criteria.runs > 1 ? 's' : ''}`);
  }
  if (criteria.total_miles) {
    parts.push(`Travel ${criteria.total_miles} miles`);
  }
  if (criteria.unique_trails) {
    parts.push(`Complete ${criteria.unique_trails} unique trail${criteria.unique_trails > 1 ? 's' : ''}`);
  }
  if (criteria.photos) {
    parts.push(`Share ${criteria.photos} photo${criteria.photos > 1 ? 's' : ''}`);
  }
  if (criteria.condition_reports) {
    parts.push(`Submit ${criteria.condition_reports} condition report${criteria.condition_reports > 1 ? 's' : ''}`);
  }
  if (criteria.active_months) {
    parts.push(`Be active for ${criteria.active_months} month${criteria.active_months > 1 ? 's' : ''}`);
  }
  if (criteria.runs_per_month) {
    parts.push(`Log ${criteria.runs_per_month}+ runs/month`);
  }
  if (criteria.winter_runs) {
    parts.push(`Complete ${criteria.winter_runs} winter run${criteria.winter_runs > 1 ? 's' : ''}`);
  }
  if (criteria.weekend_runs) {
    parts.push(`Complete ${criteria.weekend_runs} weekend run${criteria.weekend_runs > 1 ? 's' : ''}`);
  }
  if (criteria.season) {
    const seasonMap: Record<string, string> = {
      winter: 'in winter',
      spring: 'in spring', 
      summer: 'in summer',
      fall: 'in fall'
    };
    if (parts.length > 0 && seasonMap[criteria.season]) {
      parts[parts.length - 1] += ` ${seasonMap[criteria.season]}`;
    }
  }
  
  return parts.join(' • ') || 'Special achievement';
};

// Calculate progress towards an achievement
const getProgress = (
  criteria: Record<string, any> | undefined,
  userMetrics: UserMetrics | undefined
): { current: number; target: number; percentage: number } | null => {
  if (!criteria || !userMetrics) return null;
  
  // Check each criteria type and return progress for the first match
  if (criteria.runs && userMetrics.totalRuns !== undefined) {
    const current = userMetrics.totalRuns;
    const target = criteria.runs;
    return { current, target, percentage: Math.min(100, (current / target) * 100) };
  }
  
  if (criteria.total_miles && userMetrics.totalMiles !== undefined) {
    const current = Math.round(userMetrics.totalMiles);
    const target = criteria.total_miles;
    return { current, target, percentage: Math.min(100, (current / target) * 100) };
  }
  
  if (criteria.unique_trails && userMetrics.trailsCompletedCount !== undefined) {
    const current = userMetrics.trailsCompletedCount;
    const target = criteria.unique_trails;
    return { current, target, percentage: Math.min(100, (current / target) * 100) };
  }
  
  if (criteria.photos && userMetrics.photosShared !== undefined) {
    const current = userMetrics.photosShared;
    const target = criteria.photos;
    return { current, target, percentage: Math.min(100, (current / target) * 100) };
  }
  
  if (criteria.active_months && userMetrics.activeMonthsThisYear !== undefined) {
    const current = userMetrics.activeMonthsThisYear.count;
    const target = criteria.active_months;
    return { current, target, percentage: Math.min(100, (current / target) * 100) };
  }
  
  return null;
};

export const ProfileAchievements = ({
  earnedAchievements,
  availableAchievements,
  isLoading,
  userMetrics,
}: ProfileAchievementsProps) => {
  const [filter, setFilter] = useState<FilterType>('all');

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Achievements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-44 w-44 shrink-0" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalPoints = earnedAchievements?.reduce(
    (sum, ua) => sum + (ua.achievement.points || 0),
    0
  ) || 0;

  // Combine earned and available for display
  const allAchievements = [
    ...(earnedAchievements || []).map(ua => ({ 
      ...ua.achievement, 
      earned: true, 
      earnedAt: ua.earned_at 
    })),
    ...(availableAchievements || []).map(a => ({ 
      ...a, 
      earned: false, 
      earnedAt: null 
    })),
  ];

  // Apply filter
  const filteredAchievements = allAchievements.filter(achievement => {
    if (filter === 'completed') return achievement.earned;
    if (filter === 'incomplete') return !achievement.earned;
    return true;
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          Achievements
        </CardTitle>
        <div className="flex items-center gap-3">
          <ToggleGroup 
            type="single" 
            value={filter} 
            onValueChange={(value) => value && setFilter(value as FilterType)}
            className="bg-muted rounded-lg p-1"
          >
            <ToggleGroupItem value="all" className="text-xs px-3 py-1 h-7 data-[state=on]:bg-background">
              All
            </ToggleGroupItem>
            <ToggleGroupItem value="completed" className="text-xs px-3 py-1 h-7 data-[state=on]:bg-background">
              Completed
            </ToggleGroupItem>
            <ToggleGroupItem value="incomplete" className="text-xs px-3 py-1 h-7 data-[state=on]:bg-background">
              Incomplete
            </ToggleGroupItem>
          </ToggleGroup>
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
            {totalPoints} pts
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {filteredAchievements.length === 0 ? (
          <div className="text-center py-8">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
            <p className="text-muted-foreground">
              {filter === 'completed' ? 'No achievements earned yet' : 
               filter === 'incomplete' ? 'All achievements completed!' : 
               'No achievements available'}
            </p>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {filteredAchievements.map((achievement) => {
              const progress = !achievement.earned 
                ? getProgress(achievement.criteria, userMetrics) 
                : null;

              return (
                <div 
                  key={achievement.id}
                  className={`flex flex-col p-4 rounded-lg border shrink-0 w-44 transition-colors ${
                    achievement.earned 
                      ? 'bg-card border-amber-500/30' 
                      : 'bg-muted/30 opacity-80'
                  }`}
                >
                  {/* Icon */}
                  <div 
                    className={`h-10 w-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                      achievement.earned 
                        ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                        : 'bg-muted'
                    }`}
                  >
                    {achievement.earned ? (
                      <Award className="h-5 w-5 text-white" />
                    ) : (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>

                  {/* Name */}
                  <p className={`text-sm font-semibold text-center leading-tight mb-1 ${
                    achievement.earned ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {achievement.name}
                  </p>

                  {/* Points */}
                  <Badge 
                    variant={achievement.earned ? "secondary" : "outline"} 
                    className="mx-auto text-xs mb-2"
                  >
                    {achievement.points} pts
                  </Badge>

                  {/* Criteria */}
                  <p className={`text-xs text-center leading-relaxed flex-1 ${
                    achievement.earned ? 'text-muted-foreground' : 'text-muted-foreground/70'
                  }`}>
                    {formatCriteria(achievement.criteria)}
                  </p>

                  {/* Progress or Earned indicator */}
                  {achievement.earned && achievement.earnedAt ? (
                    <div className="flex items-center justify-center gap-1 mt-2 pt-2 border-t border-border/50">
                      <Check className="h-3 w-3 text-green-500" />
                      <span className="text-xs text-green-600">
                        {format(new Date(achievement.earnedAt), 'MMM d, yyyy')}
                      </span>
                    </div>
                  ) : progress ? (
                    <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                      <Progress value={progress.percentage} className="h-1.5" />
                      <p className="text-xs text-center text-muted-foreground">
                        {progress.current}/{progress.target}
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
